import React, { useCallback, useEffect, useMemo, useState } from 'react'
import type { Auth0User, UserPreferencesDocument, Permission } from 'paintscout'
import { getHighestRole, getUserRoleFlags } from '@paintscout/util'
import { resolvePermissions } from '@paintscout/util/builder'
import type { Auth0UserWithClaims, UserProfile, UserValue } from './UserContext'
import UserContext from './UserContext'

export interface UserProviderProps {
  user: Auth0User
  preferences: UserPreferencesDocument
  remainingQuotes?: number
  children?: React.ReactNode | ((context: UserValue) => React.ReactNode)
  togglePaymentExpiryNoticeCache?: () => void
  /**
   * Whether this tree is masquerading into another company. Passed in rather than read from storage
   * here: masquerade context is per-tab and owned by the app's AuthorizeUser, which is also what the
   * Apollo client is scoped to — reading storage from inside this provider could report a different
   * company than the data being rendered, and left `isMasquerading` outside the memo below so it
   * never refreshed at all.
   */
  isMasquerading?: boolean
}

export default function UserProvider({
  togglePaymentExpiryNoticeCache,
  children,
  user,
  preferences,
  remainingQuotes,
  isMasquerading
}: UserProviderProps) {
  const company = user?.app_metadata?.company as
    | (Auth0User['app_metadata']['company'] & {
        flaggedTrial?: boolean
      })
    | null
    | undefined
  const [trialExpired, setTrialExpired] = useState(false)
  const trialExpiry = company?.trialExpiry
  const subscription = company?.subscription
  const [paymentExpiryNotice, setPaymentExpiryNotice] = useState(company?.paymentExpiryNotice ?? false)
  const updatePaymentExpiryNotice = async (paymentExpiryNotice: boolean) => {
    setPaymentExpiryNotice(paymentExpiryNotice)
    if (togglePaymentExpiryNoticeCache) {
      togglePaymentExpiryNoticeCache()
    }
  }

  useEffect(() => {
    setPaymentExpiryNotice(company?.paymentExpiryNotice ?? false)
    if (togglePaymentExpiryNoticeCache) {
      togglePaymentExpiryNoticeCache()
    }
  }, [company?.paymentExpiryNotice, togglePaymentExpiryNoticeCache, user])

  useEffect(() => {
    if (trialExpired && !trialExpiry) {
      setTrialExpired(false)
    }
  }, [trialExpiry, trialExpired])

  const updateTrialExpired = useCallback(
    (date: number) => {
      const updatedTrialExpired = trialExpiry !== null && trialExpiry < date
      if (updatedTrialExpired !== trialExpired) setTrialExpired(updatedTrialExpired)
    },
    [trialExpired, trialExpiry]
  )
  const isSubscribed = company?.nonBilling
    ? true
    : (user.app_metadata?.active && (company?.active || company?.subscription?.status === 'active')) ?? false
  const isChurned = !isSubscribed && subscription?.status === 'cancelled' && subscription?.current_term_end < Date.now()

  const isLocalDev =
    process.env.NODE_ENV === 'development' ||
    (typeof window !== 'undefined' && window.location.hostname === 'localhost')

  const effectiveTrialExpired = isLocalDev ? false : trialExpired
  const effectiveIsTrial = isLocalDev ? false : company?.isTrial && isSubscribed

  const value: UserValue = useMemo(() => {
    const roles = user?.app_metadata?.roles ?? []

    return {
      user,
      preferences,
      highestRole: getHighestRole(roles),
      ...getUserRoleFlags(roles),
      isMasquerading: !!isMasquerading,
      isOwner: user?.app_metadata?.isOwner ?? false,
      painterType: user?.app_metadata?.painterType,
      isDirty: false,
      isSubscribed,
      isChurned,

      hasPermissions: hasPermissions(user),
      updateTrialExpired,
      paymentExpiryNotice,
      updatePaymentExpiryNotice,

      // legacy
      profile: getProfile({ user, preferences }),

      nonBilling: company?.nonBilling ?? false,
      isTrial: (company?.isTrial && isSubscribed) ?? false,
      flaggedTrial: company?.flaggedTrial ?? false,
      trialExpired,
      trialExpiry,
      billingId: company?.billingId,
      remainingQuotes,
      dunningStatus: company?.dunning?.status ?? 'none',
      dunning: company?.dunning
        ? {
            firstPaymentFailedAt: company.dunning.firstPaymentFailedAt,
            invoiceId: company.dunning.invoiceId,
            amountDue: company.dunning.amountDue,
            currency: company.dunning.currency,
            deactivationAt: company.dunning.deactivationAt
          }
        : undefined
    }
  }, [
    company,
    user,
    preferences,
    remainingQuotes,
    isMasquerading,
    effectiveTrialExpired,
    effectiveIsTrial,
    trialExpiry,
    subscription,
    isSubscribed,
    isChurned,
    updateTrialExpired,
    paymentExpiryNotice
  ])

  const childrenFn = children as (context: UserValue) => React.ReactNode

  return (
    <UserContext.Provider value={value}>
      {typeof children === 'function' ? childrenFn(value) : children}
    </UserContext.Provider>
  )
}

function hasPermissions(user: Auth0User) {
  return (permissions: Permission[], any?: boolean) => {
    let hasAny = false
    let hasAll = true

    const userPermissions = resolvePermissions(user?.app_metadata)

    permissions.forEach((permission: Permission) => {
      const hasPermission = userPermissions.includes(permission)
      if (!hasPermission) {
        hasAll = false
      }
      if (hasPermission) {
        hasAny = true
      }
    })

    return any ? hasAny : hasAll
  }
}

function getProfile({
  user,
  preferences
}: {
  user: Auth0UserWithClaims
  preferences: UserPreferencesDocument
}): UserProfile {
  const { app_metadata, user_metadata } = user
  const { roles } = app_metadata

  return {
    _id: app_metadata.user._id,
    firstName: user_metadata.firstName,
    lastName: user_metadata.lastName,
    phoneNumber: user_metadata.phoneNumber,
    title: user_metadata.title,
    email: user.email,
    preferences,
    roles,
    active: app_metadata ? app_metadata.active : undefined
  }
}
